<script setup>
import AppButton from '@/components/atoms/AppButton'
import AppSkeleton from '@/components/atoms/AppSkeleton'
import HelpHubChatMessage from './HelpHubChatMessage.vue'
import HelpHubChatProgress from './HelpHubChatProgress.vue'
import HelpHubChatResolution from './HelpHubChatResolution.vue'
import { conversationSubject, ticketReference } from '@/helpers/support'
import { SUPPORT_BOT_NAME } from '@/constants'

/**
 * The open conversation. Its way back is the panel's, beside the tabs (index.vue), so it
 * stays put while the thread scrolls.
 */
defineProps({
  messages: { type: Array, required: true },
  // The conversation record, for its ticket reference and subject; absent until the server
  // has one (a thread the dealer has only just started).
  conversation: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  // The dealer's line is on screen; the reply is still coming.
  awaiting: { type: Boolean, default: false },
  // What the support AI is doing, while it does it.
  progress: { type: Array, default: () => [] },
  // What went wrong — 'load', 'send', 'escalate' or 'askAi' — so the banner can say it; null when nothing did.
  failure: { type: String, default: null },
  // "Did this resolve your issue?" — shown, and which answer is in flight.
  showResolution: { type: Boolean, default: false },
  resolving: { type: String, default: null },
})

defineEmits(['retry', 'choose', 'resolve', 'dismiss-resolution'])

// What went wrong, in the dealer's terms; the AI's line is the support widget's own.
const FAILURE_COPY = {
  load: "Couldn't load this conversation.",
  send: "Your message didn't send.",
  escalate: "Couldn't reach the support team.",
  askAi: 'AI is unavailable right now. Your agent will help you shortly.',
}
</script>

<template>
  <div class="flex flex-col gap-y-4 px-4 pt-3 pb-6">
    <!-- pt-3 under the panel's sticky block (pb-3) makes Figma's 24px between the Back and the first turn. -->
    <!-- The reference an agent knows the thread by, with what it is about; -mt-3 sets it on the sticky block's own 12px, closer to the Back than a first turn's 24. -->
    <header v-if="conversation" class="-mt-3 flex flex-col gap-y-0.5 border-b border-gray-200 pb-3">
      <span v-if="ticketReference(conversation)" class="text-xs font-semibold text-gray-500" v-text="ticketReference(conversation)" />
      <h3 class="text-sm font-medium text-gray-900" v-text="conversationSubject(conversation)" />
    </header>

    <!-- A thread opened from the list has to load; one the dealer just started does not. -->
    <div v-if="loading" role="status" class="flex flex-col gap-y-4">
      <span class="sr-only">Loading conversation</span>
      <AppSkeleton width="w-2/3" height="h-10" rounded="rounded-xl" class="self-end" />
      <AppSkeleton height="h-24" rounded="rounded-xl" />
    </div>

    <ul v-else class="flex flex-col gap-y-4">
      <HelpHubChatMessage
        v-for="message in messages"
        :key="message.id"
        :message="message"
        :busy="awaiting"
        @choose="$emit('choose', $event)"
      />
    </ul>

    <HelpHubChatProgress v-if="progress.length" :steps="progress" />

    <!-- Until the first progress frame lands there is nothing to name, so this stands in. -->
    <div v-else-if="awaiting" class="flex items-center gap-x-2" role="status">
      <span class="sr-only">{{ SUPPORT_BOT_NAME }} is replying</span>

      <span class="flex size-6 shrink-0 items-center justify-center rounded-xl bg-gray-100">
        <Icon icon="cdp:support-bot" class="h-4 w-[18.46px] shrink-0 text-tint-1" />
      </span>

      <span class="flex items-center gap-x-1" aria-hidden="true">
        <span
          v-for="dot in 3"
          :key="dot"
          class="size-1.5 animate-bounce rounded-full bg-gray-300"
          :style="{ animationDelay: `${(dot - 1) * 150}ms` }"
        />
      </span>
    </div>

    <HelpHubChatResolution
      v-if="showResolution"
      :pending="resolving"
      @answer="$emit('resolve', $event)"
      @dismiss="$emit('dismiss-resolution')"
    />

    <!-- The way to a person is the footer's (index.vue), over the composer; the handoff note is a turn of the thread. -->
    <div v-if="failure" class="flex items-center justify-between gap-x-3 rounded-xl border border-red-200 bg-red-50 p-3">
      <p class="text-xs text-red-900" v-text="FAILURE_COPY[failure]" />

      <AppButton variant="link" size="sm" @click="$emit('retry')">Try again</AppButton>
    </div>
  </div>
</template>
